import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import adminTranslations from '../utils/adminTranslations';
import landingTranslations from '../utils/translations';

export type Lang = 'en' | 'ar';

interface LanguageContextType {
  lang: Lang;
  isRTL: boolean;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  t: (key: string) => string;
}

// Merge landing + admin dictionaries so any key from either is available.
const dictionaries: Record<string, Record<string, string>> = {
  en: { ...landingTranslations.en, ...adminTranslations.en },
  ar: { ...landingTranslations.ar, ...adminTranslations.ar },
};

const applyDirection = (lang: Lang) => {
  if (lang === 'ar') {
    document.body.setAttribute('dir', 'rtl');
    document.body.classList.add('rtl');
  } else {
    document.body.setAttribute('dir', 'ltr');
    document.body.classList.remove('rtl');
  }
};

const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  isRTL: false,
  setLang: () => {},
  toggleLang: () => {},
  t: (key: string) => key,
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(() => (localStorage.getItem('locale') as Lang) || 'en');

  // Apply direction on mount + whenever the language changes (no reload required).
  useEffect(() => {
    applyDirection(lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    localStorage.setItem('locale', next);
    applyDirection(next);
    setLangState(next);
  }, []);

  const toggleLang = useCallback(() => {
    setLang(lang === 'ar' ? 'en' : 'ar');
  }, [lang, setLang]);

  const t = useCallback(
    (key: string): string => dictionaries[lang]?.[key] || dictionaries['en'][key] || key,
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, isRTL: lang === 'ar', setLang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
